import Vue from 'vue'

// ordit1/tasks/tasks#120: one `purchase` dataLayer event per confirmed order and browser. The backend decides
// (order.analytics); this module only passes the decision on. Every export catches its own errors: analytics
// must never break checkout or the summary page.
const PENDING_PREFIX = 'ordit_purchase_pending:'
const SENT_KEY = 'ordit_purchase_sent'
const LOCK_NAME = 'ordit_purchase'
const PENDING_TTL_MS = 24 * 60 * 60 * 1000
const SENT_LIMIT = 50
const CANCELLED_STATUSES = ['cancelled', 'cancelled-after-approval']

function isImpersonating() {
    return !!Vue.prototype.$impersonate?.state?.isActive
}

// One key per pending order, so tabs never overwrite each other's entries.
function isPending(id) {
    try {
        const placedAt = Number(window.localStorage.getItem(PENDING_PREFIX + id))
        return Number.isFinite(placedAt) && placedAt > 0 && Date.now() - placedAt < PENDING_TTL_MS
    } catch (e) {
        return false
    }
}

function removePending(id) {
    try {
        window.localStorage.removeItem(PENDING_PREFIX + id)
    } catch (e) {
        // storage blocked
    }
}

function dropExpiredPending() {
    try {
        const now = Date.now()
        const keys = []
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i)
            if (key && key.indexOf(PENDING_PREFIX) === 0) keys.push(key)
        }
        keys.forEach((key) => {
            const placedAt = Number(window.localStorage.getItem(key))
            if (!Number.isFinite(placedAt) || now - placedAt >= PENDING_TTL_MS)
                window.localStorage.removeItem(key)
        })
    } catch (e) {
        // storage blocked
    }
}

function readSent() {
    try {
        const value = JSON.parse(window.localStorage.getItem(SENT_KEY) || '[]')
        return Array.isArray(value) ? value : []
    } catch (e) {
        return []
    }
}

// Durable de-duplication: true only if the id is really stored.
function persistSent(sent, id) {
    try {
        window.localStorage.setItem(SENT_KEY, JSON.stringify([...sent, id].slice(-SENT_LIMIT)))
        return readSent().includes(id)
    } catch (e) {
        return false
    }
}

function amount(value) {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}

export function buildPurchasePayload(order) {
    const a = order.analytics
    const companyPaid = amount(order.price_sum_firm)
    const userPaid = amount(order.price_sum_user)
    const brand = order.restaurant?.name
    const items = (Array.isArray(order.items) ? order.items : []).map((item) => {
        const out = { item_name: item.name, quantity: item.quantity }
        if (item.meal?.id) out.item_id = String(item.meal.id)
        if (brand) out.item_brand = brand
        return out
    })
    const payload = {
        event: 'purchase',
        ecommerce: {
            transaction_id: String(order.id),
            value: amount(order.price_sum_items),
            currency: 'GBP',
            customer_type: a.first_user_purchase ? 'new' : 'returning',
            items,
        },
        order_total: amount(order.price_sum),
        order_company_paid: companyPaid,
        order_user_paid: userPaid,
        ordit_customer_relationship: a.customer_relationship,
        funding: companyPaid > 0 ? (userPaid > 0 ? 'mixed' : 'company') : 'self',
        first_user_purchase: a.first_user_purchase,
        first_user_purchase_at_company: a.first_user_purchase_at_company,
        first_company_purchase: a.first_company_purchase,
    }
    if (a.company_id) payload.company_id = String(a.company_id)
    return payload
}

export function markPurchasePending(orderId) {
    try {
        if (!orderId || isImpersonating()) return
        window.localStorage.setItem(PENDING_PREFIX + orderId, String(Date.now()))
    } catch (e) {
        // never break checkout
    }
}

function reportUnlocked(order, isRelevant) {
    try {
        if (!order || isImpersonating() || !isRelevant()) return 'skipped'
        const id = String(order.id)
        dropExpiredPending()
        if (!isPending(id)) return 'skipped'
        const sent = readSent()
        if (sent.includes(id)) {
            removePending(id)
            return 'skipped'
        }
        if (!order.analytics) return 'skipped'
        if (order.analytics.purchase_confirmed === true) {
            const payload = buildPurchasePayload(order)
            if (!persistSent(sent, id)) return 'skipped' // no durable de-duplication: never risk a second push
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({ ecommerce: null })
            window.dataLayer.push(payload)
            removePending(id)
            return 'sent'
        }
        if (CANCELLED_STATUSES.includes(order.order_status)) {
            removePending(id)
            return 'dropped'
        }
        return 'pending'
    } catch (e) {
        return 'skipped'
    }
}

// Serialised across tabs (Web Locks API) so two tabs cannot both push one order; storage is re-read and
// isRelevant() re-evaluated inside the lock. Browsers without navigator.locks run unlocked (sequential tabs are
// still de-duplicated by the sent list).
export function reportPurchase(order, isRelevant) {
    try {
        if (window.navigator?.locks?.request) {
            return window.navigator.locks
                .request(LOCK_NAME, () => reportUnlocked(order, isRelevant))
                .catch(() => 'skipped')
        }
    } catch (e) {
        // fall through to the unlocked run
    }
    return Promise.resolve(reportUnlocked(order, isRelevant))
}
